import { useContext, useEffect } from 'react';
import type { CommandIds, SingularHandler } from '../types/Command';
import { CommandRegistryContext } from './CommandRegistryContext';

type Props = {
  /**
   * Disable all handlers registered to this Command and hide it from the help
   * modal.
   */
  disabled?: boolean;
  /** Command ID, one of the options in `COMMAND_LIST.ts` */
  id: CommandIds;
  /** Callback function to be called when the user presses the target */
  onKeyDown?: SingularHandler;
  /** Callback function when user releases target */
  onKeyUp?: SingularHandler;
};

/**
 * Connects your function to a command that's named in the `COMMAND_LIST`.
 *
 * @example
 * ```
 * const onKeyDown = useCallback((evt) => toggle(), [toggle]);
 * return (
 *   <>
 *     <Button onClick={toggle}>Toggle Modal</Button>
 *     <KeyboardShortcut id="my.id" onKeyDown={onKeyDown} />
 *   </>
 * );
 * ```
 */
export function KeyboardShortcut({ disabled, id, onKeyDown, onKeyUp }: Props) {
  const registry = useContext(CommandRegistryContext);

  useEffect(() => {
    const command = registry?.get(id);
    if (registry && command && command?.disabled !== disabled) {
      registry.setDisabled(id, disabled);
    }
  }, [disabled, id, registry]);

  useEffect(() => {
    const ref = registry;
    const handler = { onKeyDown, onKeyUp };

    if (id && ref && handler) {
      ref.assignHandler(id, handler);
    }

    return () => {
      if (id && ref && handler) {
        ref.removeHandler(id, handler);
      }
    };
  }, [id, onKeyDown, onKeyUp, registry]);

  return null;
}
